import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, UserPlus, UserMinus, Trophy, TrendingUp, Flame, Coins, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { communityApi, MyStats } from '../api';
import { statsApi } from '../api';
import { Card, IconBadge } from '../components/ui';
import { formatHandicapIndex } from '../utils/handicapIndex';

interface PublicGolfer {
  id: number;
  full_name: string;
  handicap?: number;
  home_club?: string;
  avatar_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  x_url?: string;
  tiktok_url?: string;
  is_in_circle?: boolean;
}

const GolferProfile: React.FC = () => {
  const params = useParams();
  const userId = Number(params.id);

  const [user, setUser] = useState<PublicGolfer | null>(null);
  const [stats, setStats] = useState<MyStats | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setError('');
      setIsLoading(true);
      try {
        if (!userId) {
          setError('Invalid user');
          return;
        }


        const [profileRes, statsRes] = await Promise.all([
          communityApi.getUserProfile(userId),
          statsApi.getUserStats(userId),
        ]);

        setUser(profileRes);
        setStats(statsRes);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [userId]);

  const handleFollowToggle = async () => {
    if (!user) return;
    setError('');
    setIsSaving(true);
    try {

      if (user.is_in_circle) {
        await communityApi.unfollowUser(user.id);
        setUser({ ...user, is_in_circle: false });
      } else {
        await communityApi.followUser(user.id);
        setUser({ ...user, is_in_circle: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update circle');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="pb-20 space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/golfers" className="inline-flex items-center space-x-2 text-sm text-green-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Link>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : !user ? (
          <Card padding="lg">
            <p className="text-sm text-gray-500">User not found.</p>
          </Card>
        ) : (
          <>
            <div className="bg-gradient-to-br from-brand-800 to-brand-950 rounded-3xl border border-accent-400/20 p-6 text-white text-center relative">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold">{user.full_name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <h1 className="text-2xl font-bold">{user.full_name}</h1>
              <p className="opacity-90 mt-1 text-sm">
                {user.home_club ? user.home_club : 'Golf buddy'}
                {typeof user.handicap === 'number' ? ` • Handicap Index ${formatHandicapIndex(user.handicap)}` : ''}
              </p>

              <div className="mt-4 flex justify-center space-x-3">
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={handleFollowToggle}
                  className="inline-flex items-center space-x-2 bg-white text-emerald-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-100 transition disabled:opacity-50"
                >
                  {user.is_in_circle ? <UserMinus className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                  <span>{user.is_in_circle ? 'Remove' : 'Add to circle'}</span>
                </button>

                <Link
                  to={`/messages/new/${user.id}`}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition ${
                    user.is_in_circle ? 'bg-emerald-900/30 text-white hover:bg-emerald-900/40' : 'bg-white/20 text-white opacity-70 pointer-events-none'
                  }`}
                  title={user.is_in_circle ? 'Message' : 'Add to circle to message'}
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Message</span>
                </Link>
              </div>
            </div>

            <Card padding="lg" className="p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Stats</h2>
              {!stats ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <IconBadge><TrendingUp /></IconBadge>
                      <span className="text-xs text-gray-500">Win rate</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{Math.round(stats.win_rate * 100)}%</p>
                    <p className="text-xs text-gray-500">{stats.wins}/{stats.games_completed} completed</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <IconBadge tone="gold"><Flame /></IconBadge>
                      <span className="text-xs text-gray-500">Streak</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{stats.current_win_streak}</p>
                    <p className="text-xs text-gray-500">wins in a row</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <IconBadge><Coins /></IconBadge>
                      <span className="text-xs text-gray-500">Rounds</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{stats.games_completed}</p>
                    <p className="text-xs text-gray-500">completed</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <IconBadge tone="gold"><Trophy /></IconBadge>
                      <span className="text-xs text-gray-500">Best round</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{stats.best_round_strokes ?? '—'}</p>
                    <p className="text-xs text-gray-500">strokes</p>
                  </div>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default GolferProfile;
